import { blotterPreferenceStatePersistenceApiConnector } from 'apiConnector/blotters/blotterPreferenceStatePersistance';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { EMPTY, of } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import type { SpEpic } from 'shared/middlewares/epic/epicMiddleware.factory';
import {
  secondaryMarketOrderBlotterPreferenceStateGet,
  secondaryMarketOrderBlotterPreferenceStateLoaded,
  secondaryMarketOrderBlotterPreferenceStateLoadingFailed,
  secondaryMarketOrderBlotterPreferenceStatePersisted,
} from 'state/blotters/secondaryMarketOrderBlotter';
import type { EpicWithThunk } from 'state/state.common';

import { toBlotterPreferences } from '../../shared/preferenceState/preferenceStateEpics.helper';

// TODO catch Error in Epic
export function persistPreferenceStateEpic(
  aggregationFrequency: number = 1000,
  scheduler?: SchedulerLike,
): EpicWithThunk {
  return (
    actions$,
    state$,
    {
      httpClient,
      selectors: {
        fromSecondaryOrderBlotter: { canSaveBlotterPreferences },
      },
    },
  ) =>
    actions$.pipe(
      ofType('SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_PERSISTED'),
      withLatestFrom(state$),
      filter(([, state]) => canSaveBlotterPreferences(state)),
      debounceTime(aggregationFrequency, scheduler),
      mergeMap(([action]) => {
        return blotterPreferenceStatePersistenceApiConnector(httpClient)
          .saveState(action.blotterPreferenceState, 'orders')
          .pipe(
            mergeMap(_ => EMPTY),
            catchError(_ => EMPTY),
          );
      }),
    );
}

export function loadPreferenceStateEpic(): EpicWithThunk {
  return (actions$, _state$, { httpClient }) =>
    actions$.pipe(
      ofType('SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_GET'),
      mergeMap(_ => {
        return blotterPreferenceStatePersistenceApiConnector(httpClient)
          .getState('orders')
          .pipe(
            map(response => secondaryMarketOrderBlotterPreferenceStateLoaded(toBlotterPreferences(response))),
            catchError(_ => of(secondaryMarketOrderBlotterPreferenceStateLoadingFailed())),
          );
      }),
    );
}

export function columnStateChangedEpic(): EpicWithThunk {
  return (action$, state$, { selectors: { getSecondaryMarketOrderBlotterPreferenceState } }) =>
    action$.pipe(
      ofType('SECONDARY_MARKET_ORDER_BLOTTER_COLUMN_STATE_CHANGED'),
      withLatestFrom(state$),
      mergeMap(([, state]) => {
        const blotterPreferenceState = getSecondaryMarketOrderBlotterPreferenceState(state);
        return of(secondaryMarketOrderBlotterPreferenceStatePersisted(blotterPreferenceState)).pipe(
          catchError(_ => EMPTY),
        );
      }),
    );
}

export function fetchColumnDefinitionReceivedEpic(): SpEpic {
  return action$ =>
    action$.pipe(
      ofType('SECONDARY_MARKET_ORDER_BLOTTER_COLUMN_DEFINITION_RECEIVED'),
      map(secondaryMarketOrderBlotterPreferenceStateGet),
    );
}
